/**
 * The words an inventory has taught itself: its tags, and the field templates
 * somebody saved.
 *
 * Kept beside the taxonomy rather than inside it because they change for
 * different reasons -- a tag appears by being worn, on an item screen, while a
 * location is made deliberately -- and the taxonomy is what capture waits on.
 * Nothing here is on the capture path, so a slow or failed load costs a
 * suggestion, never a save.
 */

import { useCallback, useEffect, useState } from "react";
import type { FieldTemplate, FieldTemplateCreateRequest, ReserveTag, TagCount } from "@iltocat/shared";
import { api } from "./api.js";

export interface Vocabulary {
  tags: TagCount[];
  templates: FieldTemplate[];
  /** A stockroom's reserve tags; empty anywhere else. */
  reserveTags: ReserveTag[];
  reloadReserveTags: () => Promise<void>;
  /** Tags move when an item is saved, and this is what that screen calls afterwards. */
  reloadTags: () => Promise<void>;
  saveTemplate: (template: FieldTemplateCreateRequest) => Promise<void>;
  removeTemplate: (id: string) => Promise<void>;
}

export function useVocabulary(stock = false): Vocabulary {
  const [tags, setTags] = useState<TagCount[]>([]);
  const [templates, setTemplates] = useState<FieldTemplate[]>([]);

  const reloadTags = useCallback(async () => {
    try {
      setTags((await api.tags.list()).tags);
    } catch {
      // A missing suggestion list is not an error worth interrupting anybody for.
    }
  }, []);

  useEffect(() => {
    void reloadTags();
    void api.fieldTemplates
      .list()
      .then(({ templates: found }) => setTemplates(found))
      .catch(() => undefined);
  }, [reloadTags]);

  const saveTemplate = useCallback(async (template: FieldTemplateCreateRequest) => {
    setTemplates((await api.fieldTemplates.save(template)).templates);
  }, []);

  const removeTemplate = useCallback(async (id: string) => {
    setTemplates((await api.fieldTemplates.remove(id)).templates);
  }, []);

  // A stockroom's reserve tags, for the item screen's tag box to offer with
  // their icon. Nothing is asked for in a personal inventory, which has none.
  const [reserveTags, setReserveTags] = useState<ReserveTag[]>([]);
  const reloadReserveTags = useCallback(async () => {
    if (!stock) return setReserveTags([]);
    try {
      setReserveTags((await api.reserves.tags()).reserveTags);
    } catch {
      // As with tags: a missing suggestion is not worth an error.
    }
  }, [stock]);
  useEffect(() => {
    void reloadReserveTags();
  }, [reloadReserveTags]);

  return { tags, templates, reserveTags, reloadTags, reloadReserveTags, saveTemplate, removeTemplate };
}
